import styled from "styled-components";
import { trackOutboundClick } from "../../utils/analytics";
import { Bell, Share2, ExternalLink } from "lucide-react";
import { Container } from "../layout/Container";
import { relFromStoreLinkPolicy } from "../../utils/storeOutboundLink";

const Section = styled.section`
  background: hsl(${({ theme }) => theme.colors.navDark});
  border-bottom: 1px solid hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.1);
`;

const Inner = styled(Container)`
  padding: 2rem 1rem;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const LogoBox = styled.div`
  width: 6rem;
  height: 6rem;
  border-radius: 1rem;
  background: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Logo = styled.img`
  width: 4rem;
  height: 4rem;
  object-fit: contain;
`;

const H1 = styled.h1`
  margin: 0;
  font-size: 1.875rem;
  font-weight: 800;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground});
`;

const Sub = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.accent});
`;

const Meta = styled.div`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.5);
  display: flex;
  gap: 1rem;
`;

const Actions = styled.div`
  display: none;
  align-items: center;
  gap: 0.75rem;
  @media (min-width: 768px) {
    display: flex;
  }
`;

const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.2);
  font-size: 0.875rem;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.7);
  &:hover {
    background: hsl(${({ theme }) => theme.colors.navDarkForeground} / 0.05);
  }
`;

const PrimaryA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  font-size: 0.875rem;
  font-weight: 600;
  &:hover {
    opacity: 0.9;
  }
`;

const VisitDisabled = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: hsl(${({ theme }) => theme.colors.primary} / 0.45);
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  font-size: 0.875rem;
  font-weight: 600;
  cursor: not-allowed;
  opacity: 0.85;
`;

export default function StoreHeroSection({ store, highlights }) {
  const websiteUrl = (store.websiteUrl || "").trim();
  const outboundRel = relFromStoreLinkPolicy(store.linkRelType);

  return (
    <Section>
      <Inner>
        <Top>
          <Left>
            <LogoBox>
              <Logo src={store.logo} alt={store.name} />
            </LogoBox>
            <div>
              <H1>{store.name} Promo Codes</H1>
              <Sub>Saving Tips & Hacks</Sub>
              <Meta>
                <span>{highlights.totalOffers} offers available</span>
                <span>•</span>
                <span>{highlights.couponCodes} coupon codes</span>
              </Meta>
            </div>
          </Left>
          <Actions>
            <GhostBtn type="button">
              <Bell size={16} /> Set Alert
            </GhostBtn>
            <GhostBtn type="button">
              <Share2 size={16} /> Share
            </GhostBtn>
            {websiteUrl ? (
              <PrimaryA
                href={websiteUrl}
                target="_blank"
                rel={outboundRel}
                onClick={() =>
                  trackOutboundClick({
                    url: websiteUrl,
                    label: `Visit ${store.name}`,
                    storeSlug: store.slug,
                  })
                }
              >
                <ExternalLink size={16} /> Visit {store.name}
              </PrimaryA>
            ) : (
              <VisitDisabled title="Add a store website URL in admin to enable this link">
                <ExternalLink size={16} /> Visit {store.name}
              </VisitDisabled>
            )}
          </Actions>
        </Top>
      </Inner>
    </Section>
  );
}
